/**
 * PeekHeader - the index preview header. Every list/peek split uses it.
 *
 *   [icon] [LABEL]  [optional chip(s)]          [... open close]
 *
 * The header owns the button cluster. A surface turns overflow or open off by
 * omitting the prop; it does not draw MoreHorizontal / ArrowUpRight / X itself.
 * `row` puts it on ShellIndexRow so it shares a line with the folder filter row.
 * Document recipe: `DocumentPeekHeader`. Gallery: /?test=maater-ui#panel-collapse
 */
import type { MouseEvent, ReactNode } from "react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
} from "@/lib/menu-chrome"
import { MoreHorizontal, ArrowUpRight, X } from "lucide-react"
import { ShellIndexRow } from "./shell-index-row"

export interface PeekHeaderProps {
  /** Small icon to the left of the label (e.g. <FileText className="h-4 w-4" />) */
  icon?: ReactNode
  /** ALL-CAPS label like "BET PREVIEW", "NOTE PREVIEW". Omit when the heading is crumbs / title only. */
  label?: string
  /** Optional chip(s) rendered after the label (e.g. section badge, type badge) */
  chips?: ReactNode
  /** Extra controls before Open and Close (for example a thread actions menu). */
  actions?: ReactNode
  /** Dropdown menu items. When omitted the ellipsis button is hidden. */
  dropdownContent?: ReactNode
  /** data-testid for the ellipsis trigger */
  dropdownTestId?: string
  /** Called when the open button is clicked. Hidden when omitted. Forwards the click so Cmd/Ctrl can resolve new vs current tab. */
  onOpen?: (event: MouseEvent<HTMLButtonElement>) => void
  /** When true, open stays visible but non-interactive (no destination). */
  openDisabled?: boolean
  /** Tooltip for the open button */
  openTitle?: string
  /** data-testid for the open button */
  openTestId?: string
  /** Called when the close button is clicked */
  onClose: () => void
  /** Tooltip for the close button */
  closeTitle?: string
  /** Match the folder filter row so the peek header sits on that same line. */
  row?: boolean
  /** data-testid for the header row. */
  testId?: string
}

export function PeekHeader({
  icon,
  label,
  chips,
  actions,
  dropdownContent,
  dropdownTestId,
  onOpen,
  openDisabled = false,
  openTitle = "Open in tab",
  openTestId,
  onClose,
  closeTitle = "Close",
  row = false,
  testId,
}: PeekHeaderProps) {
  const content = (
    <>
      <div
        className={
          label
            ? 'flex min-w-0 w-full flex-wrap items-center gap-x-2 gap-y-1 sm:flex-1 sm:flex-nowrap'
            : 'flex min-w-0 w-full flex-1 items-center'
        }
      >
        {icon ? <span className="flex shrink-0">{icon}</span> : null}
        {label ? (
          <h2 className="min-w-0 flex-1 basis-[min(100%,12rem)] truncate text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            {label}
          </h2>
        ) : null}
        {chips ? (
          <div className={label ? 'flex min-w-0 max-w-full items-center gap-1' : 'flex min-w-0 flex-1 items-center'}>
            {chips}
          </div>
        ) : null}
      </div>

      <div
        className={cn(
          'flex shrink-0 items-center justify-end gap-1',
          label ? 'w-full sm:w-auto' : 'w-auto',
        )}
      >
        {actions}
        {dropdownContent && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs"
                title="More actions"
                aria-label="More actions"
                data-testid={dropdownTestId}
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
              collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
            >
              {dropdownContent}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {onOpen && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            title={openDisabled ? "No file to open" : openTitle}
            aria-label={openDisabled ? "No file to open" : openTitle}
            data-testid={openTestId}
            disabled={openDisabled}
            onClick={openDisabled ? undefined : onOpen}
          >
            <ArrowUpRight className="h-4 w-4" />
          </Button>
        )}

        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          title={closeTitle}
          aria-label={closeTitle}
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </>
  )

  if (row) {
    return (
      <ShellIndexRow testId={testId} className="justify-between bg-card">
        {content}
      </ShellIndexRow>
    )
  }

  return (
    <div
      data-testid={testId}
      className={cn(
        'border-b border-border bg-card flex min-w-0 flex-shrink-0',
        label
          ? 'flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-x-3 sm:gap-y-0'
          : 'flex-row items-center justify-between gap-2 px-4 py-3',
      )}
    >
      {content}
    </div>
  )
}
